/**
 * PATH: src/pages/pricing/BillingDashboard.jsx
 * PURPOSE: User billing history + subscription overview
 * UI: Material UI + InvoiceList + subscription viewer
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  CircularProgress,
  Grid,
  Button,
} from "@mui/material";

import { getSubscriptions, getTransactions, cancelSubscription } from "./utils/pricingApi";
import InvoiceList from "./components/InvoiceList";

export default function BillingDashboard() {
  const [subs, setSubs] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(null);

  useEffect(() => {
    Promise.all([getSubscriptions(), getTransactions()])
      .then(([s, t]) => {
        setSubs(s);
        setTxs(t);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleCancel = async (subId) => {
    setCancelLoading(subId);
    await cancelSubscription(subId);
    const updated = await getSubscriptions();
    setSubs(updated);
    setCancelLoading(null);
  };

  if (loading)
    return (
      <Box textAlign="center" mt={8}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box p={4}>
      <Typography variant="h4" fontWeight={700} mb={3}>
        Billing Dashboard
      </Typography>

      {/* Subscriptions Section */}
      <Card elevation={3} sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Your Subscriptions
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {subs.length === 0 && (
            <Typography color="text.secondary">No active subscriptions.</Typography>
          )}

          {subs.map((sub) => (
            <Box
              key={sub._id}
              p={2}
              mb={2}
              sx={{ border: "1px solid #ddd", borderRadius: 2 }}
            >
              <Grid container alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h6">{sub.planCode}</Typography>

                  <Typography variant="body2" color="text.secondary">
                    Status:{" "}
                    <Chip
                      label={sub.status}
                      color={
                        sub.status === "active"
                          ? "success"
                          : sub.status === "cancelled"
                          ? "warning"
                          : "default"
                      }
                      size="small"
                    />
                  </Typography>

                  <Typography variant="body2" mt={1}>
                    Next Renewal: {sub.nextRenewalDate?.slice(0, 10) || "N/A"}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4} textAlign="right">
                  {sub.status === "active" && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      sx={{ borderRadius: 2 }}
                      disabled={cancelLoading === sub._id}
                      onClick={() => handleCancel(sub._id)}
                    >
                      {cancelLoading === sub._id ? (
                        <CircularProgress size={20} />
                      ) : (
                        "Cancel Subscription"
                      )}
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Transactions Section */}
      <Card elevation={3} sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Payment History
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <InvoiceList invoices={txs} />
        </CardContent>
      </Card>
    </Box>
  );
}
